import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BackendService } from '../../core/backend.service';
import { CONFIG } from '../../core/config';
import { EquipmentFunctionModelSelectionCriteriaBase } from '../../viewmodels/equipment/equipment-function-model-selection-criteria-base';
import { EquipmentLibrary } from '../../viewmodels/equipment/equipment-library';
import { LookupItem } from '../../viewmodels/static-lookups/lookup-item';
import { DynamicFormControlService } from '../dynamic-forms/dynamic-form-control.service';
import { DropdownControl } from '../dynamic-forms/form-models/dropdown-control';
import { FormControlBase } from '../dynamic-forms/form-models/form-control-base';
import { PropertyPath } from '../dynamic-forms/form-models/property-path';
import { IIsoHierarchyDataSelectionLoadService } from './iso-hierarchy-data-selection-load-service';

@Component({
  selector: 'hb-iso-hierarchy-query',
  templateUrl: './iso-hierarchy-query.component.html',
  styleUrls: ['./iso-hierarchy-query.component.scss'],
})
export class IsoHierarchyQueryComponent implements OnDestroy {
  @Input() formGroup: FormGroup;
  @Input()
  set selectionService(value: IIsoHierarchyDataSelectionLoadService) {
    this.initialize(value);
  }
  private _isEditable: boolean = true;

  get isEditable(): boolean {
    return this._isEditable;
  }

  @Input() set isEditable(value: boolean) {
    this._isEditable = value;
    this.libraryControl.disabled = !value;
  }

  public libraryControl = new DropdownControl({
    key: 'library',
    label: this.i18NextPipe.transform('IsoHierarchy.Library'),
    propertyName: PropertyPath.get((m: EquipmentFunctionModelSelectionCriteriaBase) => m.equipmentLibraryId),
    required: true,
  });

  public criteria: EquipmentFunctionModelSelectionCriteriaBase;
  private controls: FormControlBase<any>[] = [this.libraryControl];
  private dataLoadService: IIsoHierarchyDataSelectionLoadService;
  private selectionsChanged = new Subject();
  private criteriaChanged = new Subject();
  private unsubscribe: Subject<boolean> = new Subject();

  constructor(
    private backendService: BackendService,
    private dynamicFormControlService: DynamicFormControlService,
    private changeDetectorRef: ChangeDetectorRef,
    private i18NextPipe: I18NextPipe,
  ) {}

  initialize(dataLoadService: IIsoHierarchyDataSelectionLoadService) {
    this.dataLoadService = dataLoadService;

    this.loadEquipmentLibraries();

    this.dynamicFormControlService.addControls(this.formGroup, this.controls);

    this.libraryControl.onValueChanged.pipe(takeUntil(this.unsubscribe)).subscribe(value => {
      this.criteria.equipmentLibraryId = value as string;
      this.selectionsChanged.next();
    });

    this.initialiseCriteria();

    this.dataLoadService.recreateCriteriaRequested.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.initialiseCriteria();
    });
  }

  initialiseCriteria(): void {
    const newCriteria = this.dataLoadService.createCriteria();
    newCriteria.excludedModelIds = this.dataLoadService.excludedDataIds;

    if (this.criteria) {
      this.criteria.loadFromJson(newCriteria);
    } else {
      this.criteria = newCriteria;
    }

    this.dynamicFormControlService.setModel(this.criteria, this.controls);
    this.findData();

    this.changeDetectorRef.detectChanges();

    this.detectSelectionChanges();
  }

  ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  onIndustryHierarchySelectionsChanged() {
    this.selectionsChanged.next();
  }

  onEquipmentClassSelectionsChanged() {
    this.selectionsChanged.next();
  }

  private loadEquipmentLibraries() {
    this.backendService.getAll(EquipmentLibrary).subscribe(libraries => {
      this.libraryControl.options = [new LookupItem(CONFIG.constants.noneDropdownValue, this.i18NextPipe.transform('IsoHierarchy.EquipmentClass.None'))];

      if (!libraries) {
        return;
      }

      this.libraryControl.options = this.libraryControl.options.concat(
        _.orderBy(libraries, (library: EquipmentLibrary) => library.name, 'asc').map(library => new LookupItem(library.id, library.name)),
      );
    });
  }

  private detectSelectionChanges() {
    this.selectionsChanged.pipe(takeUntil(this.criteriaChanged)).subscribe(() => {
      this.findData();
    });
  }

  foundData(): boolean {
    return this.dataLoadService.validateCriteria(this.criteria) && this.criteria.hasModel();
  }

  private findData() {
    if (!this.dataLoadService || !this.criteria) {
      return;
    }

    this.dynamicFormControlService.commit(this.controls);

    if (!this.dataLoadService.validateCriteria(this.criteria)) {
      this.dataLoadService.invalidCriteriaSelected.next();
      return;
    }

    this.dataLoadService.findData(this.criteria).subscribe(criteria => {
      this.criteriaChanged.next();
      this.dynamicFormControlService.setModel(criteria, this.controls);
      this.criteria.loadFromJson(criteria);

      this.detectSelectionChanges();
      this.dataLoadService.selectionCriteriaChanged.next(this.criteria);
    });
  }
}
